import React, { useState, useEffect, useRef } from 'react'
import { connect } from 'react-redux'
import { refetchUser, checkTokenValidity, logout } from 'store/slices/authSlice'
import { getSingleUser } from 'store/slices/personalSlice'
import { tokenStorage, startTokenRefreshTimer } from 'Modules/API'
import Loading from 'Components/Loading'

/**
 * On the initial app load check if user is authenticated
 * If user is authenticated - reload user data
 * If not - just render the app
 * BLOCK any further app rendering unless user is reloaded and updated into redux store
 * Note: Parse server is no longer used, only token-based authentication
 */
function AuthGate({ children, refetchUser, checkTokenValidity, logout, getSingleUser, userLoading, isAuthenticated, currentUser }) {
  const [loadedOnce, setLoadedOnce] = useState(false)
  const [loading, setLoading] = useState(() => {
    // Check if we have a stored token
    const hasToken = tokenStorage.getAuthToken() !== null
    return hasToken
  })

  useEffect(() => {
    // Check if we have a stored token
    const hasToken = tokenStorage.getAuthToken() !== null

    if (hasToken) {
      // If we have a token, first check if it's valid, then refetch user data
      checkTokenValidity().then(isValid => {
        if (isValid) {
          refetchUser().catch((error) => {
            // If refetch fails with 401/token expired, logout and redirect
            if (error.message === 'Token expired' || error.message.includes('401') || error.message.includes('Token expired')) {
              logout()
              if (window.location.pathname !== '/login') {
                window.location.href = '/login'
              }
            }
          }).finally(() => {
            setLoading(false)
            setLoadedOnce(true)
          })
          // Start token refresh timer for persistent login
          startTokenRefreshTimer()
        } else {
          // Token is invalid, clear authentication state and redirect to login
          console.log('AuthGate: Token invalid, clearing authentication state')
          logout()
          setLoading(false)
          setLoadedOnce(true)
          if (window.location.pathname !== '/login') {
            window.location.href = '/login'
          }
        }
      }).catch(() => {
        // Token validation failed, clear authentication state and redirect to login
        console.log('AuthGate: Token validation failed, clearing authentication state')
        logout()
        setLoading(false)
        setLoadedOnce(true)
        if (window.location.pathname !== '/login') {
          window.location.href = '/login'
        }
      })
    } else {
      // No token, clear any persisted authentication state
      console.log('AuthGate: No token found, clearing authentication state')
      checkTokenValidity().catch(() => { }) // This will clear the auth state
      setLoadedOnce(true)
    }
  }, [refetchUser, checkTokenValidity, logout])

  // Set up WebSocket connection when authenticated
  // Use refs to store stable references and prevent unnecessary reconnections
  const refetchUserRef = useRef(refetchUser)
  const currentUserRef = useRef(currentUser)
  const getSingleUserRef = useRef(getSingleUser)
  
  // Update refs when values change
  useEffect(() => {
    refetchUserRef.current = refetchUser
    currentUserRef.current = currentUser
    getSingleUserRef.current = getSingleUser
  }, [refetchUser, currentUser, getSingleUser])

  // Track if WebSocket is already set up to prevent multiple connections
  const wsSetupRef = useRef(false)
  const wsUserIdRef = useRef(null) // Track which userId the WebSocket is connected for
  
  const currentUserIdProp = currentUser?.id
  const currentUserEmailProp = currentUser?.email

  useEffect(() => {
    const currentUserId = currentUserIdProp || currentUserEmailProp

    
    console.log('🔍 [WS] useEffect triggered:', {
      isAuthenticated,
      hasCurrentUser: !!currentUser,
      currentUserId,
      userLoading,
      wsSetupRef: wsSetupRef.current,
      wsUserIdRef: wsUserIdRef.current
    })
    
    if (!isAuthenticated || !currentUser || userLoading) {
      // If auth state changes to not authenticated, cleanup WebSocket
      if (wsSetupRef.current) {
        console.log('🔌 [WS] Auth state changed - cleaning up WebSocket')
        wsSetupRef.current = false
        wsUserIdRef.current = null
        import('../services/websocket').then(({ disconnectWebSocket }) => {
          disconnectWebSocket()
        })
      }
      return
    }

    // If WebSocket is already set up for the same user, don't set it up again
    if (wsSetupRef.current && wsUserIdRef.current === currentUserId) {
      console.log('🔌 [WS] WebSocket already set up for same user, skipping...', { userId: currentUserId })
      return
    }
    
    // If userId changed, disconnect old connection first
    if (wsSetupRef.current && wsUserIdRef.current !== currentUserId) {
      console.log('🔌 [WS] User changed, disconnecting old WebSocket...', { 
        oldUserId: wsUserIdRef.current, 
        newUserId: currentUserId 
      })
      import('../services/websocket').then(({ disconnectWebSocket }) => {
        disconnectWebSocket()
      })
      wsSetupRef.current = false
      wsUserIdRef.current = null
    }

    // Dynamically import WebSocket service to handle missing module gracefully
    let wsCleanup = null
    let isMounted = true

    const setupWebSocket = async () => {
      try {
        const { connectWebSocket, disconnectWebSocket } = await import('../services/websocket')
        
        console.log('🚀 [WS] Setting up WebSocket connection for web app...', { userId: currentUser.id || currentUser.email })

        const userId = currentUser.id || currentUser.email
        const token = tokenStorage.getAuthToken()

        if (!userId || !token) {
          console.warn('⚠️ [WS] No userId or token, cannot connect', { hasUserId: !!userId, hasToken: !!token })
          return
        }

        // Connect to WebSocket and listen for invalidate signals
        // Use refs to avoid recreating the callback on every render
        const socket = await connectWebSocket((payload) => {
          if (!isMounted) return // Don't process if component unmounted
          
          console.log('🔔 [WS] Invalidate signal received, refreshing data...', payload)
          console.log('🔔 [WS] Payload details:', JSON.stringify(payload, null, 2))

          // Refresh profile if needed
          if (payload.profile) {
            console.log('🔄 [WS] Refreshing profile due to invalidate signal...')
            const currentUser = currentUserRef.current
            console.log('🔄 [WS] Current user before refresh:', { 
              userId: currentUser?.id, 
              profilePropertiesCount: currentUser?.profile?.properties?.length 
            })
            
            // Use ref to get latest refetchUser function
            const refetchFn = refetchUserRef.current
            if (refetchFn) {
              console.log('🔄 [WS] Calling refetchUser to refresh profile...')
              refetchFn().then((updatedUser) => {
                if (!isMounted) return
                console.log('✅ [WS] Profile refresh completed')
                console.log('✅ [WS] Updated user after refresh:', { 
                  userId: updatedUser?.id, 
                  profilePropertiesCount: updatedUser?.profile?.properties?.length,
                  properties: updatedUser?.profile?.properties?.map(p => ({ name: p.name, objectId: p.objectId }))
                })
              }).catch((error) => {
                if (!isMounted) return
                console.error('❌ [WS] Profile refresh failed:', error)
              })
            }
          }

          // Refresh contacts/connections if needed
          if (payload.contacts) {
            console.log('🔄 [WS] Contacts update signal received, refreshing connections...')
            const currentUser = currentUserRef.current
            const getSingleUserFn = getSingleUserRef.current
            const currentUserId = currentUser?.objectId || currentUser?.id
            if (getSingleUserFn && currentUserId) {
              console.log('🔄 [WS] Calling getSingleUser to refresh connections for user:', currentUserId)
              getSingleUserFn(currentUserId).then(() => {
                if (!isMounted) return
                console.log('✅ [WS] Connections refresh completed')
              }).catch((error) => {
                if (!isMounted) return
                console.error('❌ [WS] Connections refresh failed:', error)
              })
            }
          }
        }, userId, token)

        if (socket) {
          console.log('✅ [WS] WebSocket connection set up successfully')
          wsSetupRef.current = true // Mark as set up
          wsUserIdRef.current = currentUserId // Track which user it's connected for
          
          // Log connection status periodically for debugging
          const statusInterval = setInterval(() => {
            if (!isMounted) {
              clearInterval(statusInterval)
              return
            }
            const { getWebSocketStatus } = require('../services/websocket');
            const status = getWebSocketStatus();
            console.log('🔍 [WS] Connection status check:', status);
          }, 30000); // Every 30 seconds
          
          wsCleanup = () => {
            console.log('🔌 [WS] Cleaning up WebSocket connection...')
            wsSetupRef.current = false // Mark as not set up
            wsUserIdRef.current = null // Clear userId tracking
            isMounted = false
            clearInterval(statusInterval);
            disconnectWebSocket()
          }
        } else {
          console.warn('⚠️ [WS] WebSocket connection returned null - check logs above for errors')
          wsSetupRef.current = false
          wsUserIdRef.current = null
        }
      } catch (error) {
        // Gracefully handle missing socket.io-client module
        if (error.message && error.message.includes('socket.io-client')) {
          console.warn('⚠️ [WS] socket.io-client not installed. WebSocket features disabled. Install with: yarn add socket.io-client@^4.8.1')
        } else {
          console.error('❌ [WS] Failed to set up WebSocket:', error)
        }
      }
    }

    setupWebSocket()

    // Cleanup on unmount or when auth state changes
    // Only cleanup if userId actually changed or component is unmounting
    return () => {
      const currentUserId = currentUserIdProp || currentUserEmailProp
      // Only cleanup if userId changed or we're unmounting
      if (wsUserIdRef.current !== currentUserId || !isAuthenticated) {
        console.log('🔌 [WS] Cleanup triggered:', {
          wsUserId: wsUserIdRef.current,
          currentUserId,
          isAuthenticated,
          willCleanup: wsUserIdRef.current !== currentUserId || !isAuthenticated
        })
        isMounted = false
        if (wsCleanup) {
          wsCleanup()
        }
      } else {
        console.log('🔌 [WS] Cleanup skipped - same userId, keeping connection')
      }
    }
  }, [isAuthenticated, currentUserIdProp, currentUserEmailProp, userLoading, currentUser])

  // Don't show loading indicator immediatelly to avoid "blinking"
  const [showLoading, setShowLoading] = useState(false)
  useEffect(() => {
    let timeout = setTimeout(() => setShowLoading(true), 50)
    return () => {
      if (timeout) {
        clearTimeout(timeout)
      }
    }
  }, [])

  // If user was already loaded once during app render - just render the children, because we need this gate only for initial render
  // If user loaded and redux state updated
  // If we've finished the initial load, or if we're completely done loading, show the app
  window.__AUTHGATE_RENDERED__ = true;
  console.log('[DEBUG HMI] AuthGate render state:', { loadedOnce, loading, userLoading, showLoading })
  if (loadedOnce || (!loading && !userLoading)) {
    window.__AUTHGATE_RETURNED__ = 'CHILDREN';
    console.log('[DEBUG HMI] AuthGate rendering children!')
    return (
      <>
        {children}
      </>
    )
  }

  if (showLoading) {
    window.__AUTHGATE_RETURNED__ = 'LOADING_COMPONENT';
    console.log('[DEBUG HMI] AuthGate rendering Loading component')
    return <div><Loading /></div>
  }

  window.__AUTHGATE_RETURNED__ = 'NULL';
  console.log('[DEBUG HMI] AuthGate returning null!')
  // If we are loading - initially just show blank screen, will start showing loading indicator after 50ms
  return null
}

const mapStateToProps = (state) => ({
  userLoading: state.auth.loading,
  isAuthenticated: state.auth.isAuthenticated,
  currentUser: state.auth.currentUser
})

const mapDispatchToProps = {
  refetchUser,
  checkTokenValidity,
  logout,
  getSingleUser
}

export default connect(mapStateToProps, mapDispatchToProps)(AuthGate)
